/**
 * The Live tab's data plumbing: feed + verdict report polls run only while
 * this component is mounted (i.e. the tab is on screen), keeping the page
 * itself to routing. DEV ONLY: the send simulation + simulated-day stepper
 * (?at=) when the backend has it switched on — every act reads and judges
 * the day on screen, never "the latest run".
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { SimulationBar } from '@/features/drop-generator/components/live/controls/SimulationBar';
import { LiveTabCard } from '@/features/drop-generator/components/live/LiveTabCard';
import type { DayPlanPreview } from '@/features/drop-generator/drop-generator.types';
import {
  useDgEvents,
  useVerdictReport,
} from '@/features/drop-generator/hooks/useLiveQueries';
import {
  useSimulation,
  useSimulationAvailable,
} from '@/features/drop-generator/simulation/useSimulation';
import { StopLossLine } from '@/features/drop-generator/components/live/controls/StopLossLine';
import { liveAutoSend } from '@/features/drop-generator/components/board-header/autosend/groups/group-rows';
import { usePlan, useSchedulerStatus } from '@/features/drop-generator/useDropGenerator';
import { useVerdictMutations } from '@/features/drop-generator/useVerdictMutations';

export const LiveTab: FC<{
  countryKey: string;
  at: string | null;
  onSetAt: (at: string | null) => void;
  preview: DayPlanPreview | undefined;
  previewLoading: boolean;
  previewFailed: boolean;
  onRetryPreview: () => void;
  onEditContainer: (containerId: string) => void;
  /** The open campaign's containers; null = every container. */
  scopeIds?: ReadonlySet<string> | null;
}> = ({ countryKey, at, onSetAt, scopeIds = null, ...rest }) => {
  const events = useDgEvents(countryKey);
  const eventRows = scopeIds
    ? events.data?.filter((e) => !e.containerId || scopeIds.has(e.containerId))
    : events.data;
  const report = useVerdictReport(countryKey, at);
  const plan = usePlan(countryKey).data;
  const status = useSchedulerStatus().data?.plans.find((p) => p.countryKey === countryKey);
  const autoSendOf = plan ? liveAutoSend(plan, status) : undefined;
  const simulation = useSimulationAvailable(countryKey);
  const { evaluateVerdicts, resetPool } = useVerdictMutations();
  const { simulateSends, nextDay, clearSimulation } = useSimulation();
  const simBusy =
    simulateSends.isPending || clearSimulation.isPending || nextDay.isPending;
  return (
    <div className="flex flex-col gap-4">
      {simulation.data ? (
        <SimulationBar
          at={at}
          runDate={rest.preview?.runDate}
          onNextDay={() =>
            nextDay.mutate(
              { countryKey, at },
              { onSuccess: (day) => onSetAt(day.at) },
            )
          }
          onBackToToday={() => onSetAt(null)}
          onClear={() =>
            clearSimulation.mutate(countryKey, { onSuccess: () => onSetAt(null) })
          }
          busy={simBusy}
        />
      ) : null}
      {report.data?.stopLoss ? <StopLossLine checks={report.data.stopLoss} /> : null}
      <LiveTabCard
        countryKey={countryKey}
        autoSendOf={autoSendOf}
        events={eventRows}
        eventsLoading={events.isLoading}
        eventsFailed={events.isError}
        report={report.data ?? null}
        onEvaluate={() => evaluateVerdicts.mutate({ countryKey, at })}
        evaluating={evaluateVerdicts.isPending}
        onResetPool={(containerId, listGroupId) =>
          resetPool.mutate({ countryKey, containerId, listGroupId })
        }
        resetting={resetPool.isPending}
        onSimulate={
          simulation.data
            ? (containerId, listGroupId, req) =>
                simulateSends.mutate({
                  countryKey,
                  containerId,
                  listGroupId,
                  ...req,
                  ...(at ? { at } : {}),
                })
            : undefined
        }
        simulating={simulateSends.isPending}
        {...rest}
      />
    </div>
  );
};
